import React, { useMemo } from 'react';
import { useWidgetBuilder } from '../context/WidgetBuilderContext';
import { useBuilderLabels } from '../context/BuilderLocaleContext';
import { useMatchList } from '../hooks/useMatchList';
import SearchableCombobox from './SearchableCombobox';
import { formatMatchOption, formatMatchSublabel } from './matchPickerLabels';

/** Match fixture picker — used in the competition bar for match widgets. */
export default function MatchPickerField({ className = '' }) {
  const t = useBuilderLabels();
  const { config, updateConfig } = useWidgetBuilder();
  const { data: matchListData, isLoading: matchListLoading } = useMatchList(
    config.competitionId,
    true
  );

  const matchOptions = useMemo(() => {
    const rows = matchListData?.matches || [];
    const opts = [
      {
        value: '',
        label: t('matchAuto'),
        sublabel: t('matchAutoSub'),
        group: t('groupSelection'),
      },
    ];
    for (const m of rows) {
      const id = m.matchId ? String(m.matchId) : '';
      if (!id) continue;
      opts.push({
        value: id,
        label: formatMatchOption(m),
        sublabel: formatMatchSublabel(m),
        badge: m.live ? 'live' : m.finished ? 'ft' : m.upcoming ? 'upcoming' : null,
        group: m.live
          ? t('groupLive')
          : m.finished && m.hasLineups
            ? t('groupFinishedLineups')
            : m.finished
              ? t('groupFinished')
              : t('groupUpcoming'),
      });
    }
    return opts;
  }, [matchListData?.matches, t]);

  return (
    <SearchableCombobox
      label={t('match')}
      value={config.featuredMatchId ? String(config.featuredMatchId) : ''}
      onChange={(featuredMatchId) => updateConfig({ featuredMatchId })}
      options={matchOptions}
      placeholder={t('searchMatches')}
      disabled={matchListLoading}
      emptyMessage={t('noMatches')}
      hint={
        matchListLoading
          ? t('loadingFixtures')
          : config.featuredMatchId
            ? t('previewSelectedOnly')
            : matchOptions.length > 1
              ? t('matchesPick', { count: matchOptions.length - 1 })
              : t('noFixturesAuto')
      }
      className={className}
    />
  );
}
